import { apiRequest } from '../../shared/api/client';

/**
 * Warehouse CRUD — mirrors Invetrak.Api.Endpoints.WarehouseEndpoints (camelCase).
 *   GET   /api/v1/warehouses[?includeInactive]  -> WarehouseDto[]
 *   GET   /api/v1/warehouses/{id}               -> WarehouseDto
 *   POST  /api/v1/warehouses                    -> 201 WarehouseDto | 409 dup code
 *   PATCH /api/v1/warehouses/{id}               -> WarehouseDto (code is immutable)
 *   PUT   /api/v1/warehouses/{id}/address       -> WarehouseDto
 * Reads need warehouse:read, writes warehouse:write (enforced server-side).
 */

export interface WarehouseDto {
  warehouseId: string;
  warehouseCode: string;
  displayName: string;
  timeZone: string;
  currencyCode: string;
  isActive: boolean;
  createdAtUtc: string;
  lastModifiedUtc: string;

  /**
   * The warehouse's own address — where goods ship FROM, and the return address on every packing
   * list. Null throughout when none is on file, which is a normal state.
   *
   * Also null on the record returned by create and update: those do not read the address table.
   * Only the list and the get carry it.
   */
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  stateProvinceCode: string | null;
  postalCode: string | null;
  countryCode: string | null;
}

/**
 * The whole address, replaced.
 *
 * A PUT rather than a PATCH because there is no such thing as changing the city and leaving the
 * street. Sending every field empty clears it; sending some of them is refused, because half an
 * address on a parcel is worse than none.
 */
export interface SaveWarehouseAddressRequest {
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  stateProvinceCode?: string | null;
  postalCode?: string | null;
  countryCode?: string | null;
}

export interface CreateWarehouseRequest {
  warehouseCode: string;
  displayName: string;
  timeZone?: string | null;
  currencyCode?: string | null;
  isActive?: boolean | null;
}

export interface UpdateWarehouseRequest {
  displayName: string;
  timeZone: string;
  currencyCode: string;
  isActive: boolean;
}

const COOKIE_INIT: RequestInit = { credentials: 'include' };

export async function listWarehouses(
  includeInactive = false,
  signal?: AbortSignal,
): Promise<WarehouseDto[]> {
  const query = includeInactive ? '?includeInactive=true' : '';
  const { data } = await apiRequest<WarehouseDto[]>(`/v1/warehouses/${query}`, {
    method: 'GET',
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

export async function createWarehouse(
  body: CreateWarehouseRequest,
  signal?: AbortSignal,
): Promise<WarehouseDto> {
  const { data } = await apiRequest<WarehouseDto>('/v1/warehouses/', {
    method: 'POST',
    body,
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

export async function updateWarehouse(
  id: string,
  body: UpdateWarehouseRequest,
  signal?: AbortSignal,
): Promise<WarehouseDto> {
  const { data } = await apiRequest<WarehouseDto>(`/v1/warehouses/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body,
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

export async function saveWarehouseAddress(
  id: string,
  body: SaveWarehouseAddressRequest,
  signal?: AbortSignal,
): Promise<WarehouseDto> {
  const { data } = await apiRequest<WarehouseDto>(
    `/v1/warehouses/${encodeURIComponent(id)}/address`,
    { method: 'PUT', body, signal, ...COOKIE_INIT },
  );
  return data;
}
